import { ChangeDetectionStrategy, Component, computed, effect, inject, input, model, output, signal, untracked } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { DialogModule } from 'primeng/dialog';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { SelectModule } from 'primeng/select';
import { MessageModule } from 'primeng/message';
import { CloudApiService } from '@/cloud/services/cloud-api.service';
import { LocationDto } from '@/cloud/models/tree.models';
import { ROOM_TYPES, RoomType, isRoomType } from '@/cloud/models/room-usage.models';
import { TranslationService } from '@/layout/service/translation.service';
import { translateCount } from '@/layout/service/translate-count';

/** Largest capacity the API accepts. */
export const MAX_ROOM_CAPACITY = 100_000;

/** The room the dialog edits. */
export interface RoomProfileTarget {
    id: string;
    name: string;
    capacity?: number | null;
    roomType?: string | null;
    /** Capacity the room's occupancy sensors report, shown as a hint. */
    sensorCapacity?: number | null;
}

/**
 * Parse the capacity field: blank clears it (null); otherwise a whole number 1..100000.
 * Returns undefined for anything else.
 */
export function parseRoomCapacity(value: string | number | null | undefined): number | null | undefined {
    if (value === null || value === undefined) return null;
    const text = String(value).trim();
    if (text === '') return null;
    if (!/^\d+$/.test(text)) return undefined;
    const n = Number(text);
    return n >= 1 && n <= MAX_ROOM_CAPACITY ? n : undefined;
}

/** "Room profile" dialog: a room's capacity (seats) and room type, saved with PUT /api/locations/{id}/room-profile. */
@Component({
    selector: 'app-room-profile-dialog',
    standalone: true,
    imports: [FormsModule, DialogModule, ButtonModule, InputTextModule, SelectModule, MessageModule],
    changeDetection: ChangeDetectionStrategy.OnPush,
    template: `
        <p-dialog [header]="t()('roomProfile.title')" [visible]="visible()" (visibleChange)="close($event)" [modal]="true" [closable]="!saving()" [draggable]="false" [style]="{ width: 'min(28rem, calc(100vw - 2rem))' }" appendTo="body">
            @if (room(); as room) {
                <form class="room-profile" (ngSubmit)="save()" novalidate>
                    <p class="room-profile__intro">
                        <strong>{{ room.name }}</strong> · {{ t()('roomProfile.intro') }}
                    </p>
                    <div class="room-profile__field">
                        <label for="room-profile-capacity">{{ t()('roomProfile.capacity') }}</label>
                        <input
                            id="room-profile-capacity"
                            name="capacity"
                            pInputText
                            type="number"
                            inputmode="numeric"
                            min="1"
                            [attr.max]="maxCapacity"
                            step="1"
                            [placeholder]="t()('roomProfile.capacityPlaceholder')"
                            [ngModel]="capacityText()"
                            (ngModelChange)="capacityText.set($event === null || $event === undefined ? '' : String($event))"
                            [attr.aria-invalid]="capacityInvalid()"
                            aria-describedby="room-profile-capacity-hint"
                            [disabled]="saving()"
                        />
                        <small id="room-profile-capacity-hint" [class.room-profile__error]="capacityInvalid()">
                            {{ capacityInvalid() ? t()('roomProfile.capacityInvalid') : t()('roomProfile.capacityHint') }}
                        </small>
                        @if (room.sensorCapacity && !capacityInvalid()) {
                            <small class="room-profile__sensor">{{ sensorHint() }}</small>
                        }
                    </div>
                    <div class="room-profile__field">
                        <label for="room-profile-type">{{ t()('roomProfile.type') }}</label>
                        <p-select
                            inputId="room-profile-type"
                            name="roomType"
                            [options]="typeOptions()"
                            optionLabel="label"
                            optionValue="value"
                            [ngModel]="roomType()"
                            (ngModelChange)="roomType.set($event ?? null)"
                            [showClear]="true"
                            [placeholder]="t()('roomProfile.typePlaceholder')"
                            [disabled]="saving()"
                            appendTo="body"
                            styleClass="w-full"
                        />
                    </div>
                    @if (error()) {
                        <p-message severity="error">{{ error() }}</p-message>
                    }
                    <button type="submit" hidden aria-hidden="true" tabindex="-1"></button>
                </form>
            }
            <ng-template #footer>
                <button pButton type="button" severity="secondary" (click)="close(false)" [disabled]="saving()">
                    <span class="p-button-label">{{ t()('roomProfile.cancel') }}</span>
                </button>
                <button pButton type="button" class="room-profile__save" (click)="save()" [disabled]="saving() || capacityInvalid()" [loading]="saving()">
                    <span class="p-button-label">{{ t()('roomProfile.save') }}</span>
                </button>
            </ng-template>
        </p-dialog>
    `,
    styles: [
        `
            .room-profile {
                display: flex;
                flex-direction: column;
                gap: 1rem;
            }
            .room-profile__intro {
                margin: 0;
                color: var(--text-color-secondary);
                font-size: 0.875rem;
                line-height: 1.5;
            }
            .room-profile__intro strong {
                color: var(--text-color);
            }
            .room-profile__field {
                display: flex;
                flex-direction: column;
                gap: 0.375rem;
            }
            .room-profile__field label {
                font-size: 0.875rem;
                font-weight: 600;
            }
            .room-profile__field small {
                color: var(--text-color-secondary);
                font-size: 0.75rem;
                line-height: 1.4;
            }
            .room-profile__field small.room-profile__error {
                color: var(--app-status-offline-fg);
            }
        `
    ]
})
export class RoomProfileDialogComponent {
    private api = inject(CloudApiService);
    readonly t = inject(TranslationService).t;
    readonly String = String;
    readonly maxCapacity = MAX_ROOM_CAPACITY;

    readonly visible = model(false);
    readonly room = input<RoomProfileTarget | null>(null);
    readonly saved = output<LocationDto>();

    readonly capacityText = signal('');
    readonly roomType = signal<RoomType | null>(null);
    readonly saving = signal(false);
    readonly error = signal<string | null>(null);

    readonly capacityInvalid = computed(() => parseRoomCapacity(this.capacityText()) === undefined);
    readonly typeOptions = computed(() => ROOM_TYPES.map((value) => ({ value, label: this.t()('roomType.' + value) })));
    readonly sensorHint = computed(() => translateCount(this.t(), 'roomProfile.sensorCapacity', this.room()?.sensorCapacity ?? 0));

    // Seed the form each time the dialog opens.
    private seed = effect(() => {
        if (!this.visible()) return;
        // Only on opening: a live update to the room must not reset what is being typed.
        const room = untracked(() => this.room());
        this.capacityText.set(typeof room?.capacity === 'number' ? String(room.capacity) : '');
        const roomType = room?.roomType;
        this.roomType.set(isRoomType(roomType) ? roomType : null);
        this.error.set(null);
    });

    close(visible: boolean): void {
        if (visible || this.saving()) return;
        this.visible.set(false);
    }

    async save(): Promise<void> {
        const room = this.room();
        const capacity = parseRoomCapacity(this.capacityText());
        if (!room || capacity === undefined || this.saving()) return;
        this.saving.set(true);
        this.error.set(null);
        try {
            const updated = await this.api.updateRoomProfile(room.id, { capacity, roomType: this.roomType() });
            this.saved.emit(updated);
            this.visible.set(false);
        } catch (err: unknown) {
            const detail = (err as { error?: unknown } | null)?.error;
            this.error.set(typeof detail === 'string' && detail ? detail : this.t()('roomProfile.saveFailed'));
        } finally {
            this.saving.set(false);
        }
    }
}
